import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, IconButton, MenuItem, Select, FormControl, InputLabel, Button, TextField, Avatar, Container, Divider, Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SkeletonLoader from './shared/SkeletonLoader';
import EmptyState from './shared/EmptyState';
import ErrorMessage from './shared/ErrorMessage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import { keyframes } from '@mui/system';
import { useAuth } from '../contexts/AuthContext';
import { useView } from '../contexts/ViewContext';
import { getAppointmentsForUser, createAppointment, approveAppointmentRequest, rejectAppointmentRequest, cancelAppointment, rescheduleAppointment, deleteAppointment } from '../services/appointmentsService';
import { listUsers } from '../services/userManagementService';
import { getAvailableTimeSlots } from '../services/schedulesService';
import apiClient from '../services/apiClient';
import socketClient from '../services/socketClient';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Appointments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const { getEffectiveTenantId } = useView();
  const [userType, setUserType] = useState('patient');
  const [selectedPerson, setSelectedPerson] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedTimeDate, setSelectedTimeDate] = useState(null); // For TimePicker component
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [peopleList, setPeopleList] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState(null);

  useEffect(() => {
    const type = searchParams.get('userType') || 'patient';
    setUserType(type);

    // Fetch clinics if physician
    const fetchClinics = async () => {
      if (type === 'physician' && (profile?.platform_role === 'super_admin' || profile?.tenant_role === 'physician')) {
        try {
          const response = await apiClient.get('/tenants');
          const clinicsData = response?.data?.rows || response?.data || response?.rows || [];
          // Filter out platform tenants - they're for onboarding only, not operational work
          const realClinics = clinicsData.filter((clinic) => {
            return clinic.tenant_type !== 'platform' && !clinic.is_default;
          });
          setClinics(realClinics);
          // Set default clinic to effective tenant or first clinic
          const effectiveTenantId = getEffectiveTenantId() || profile?.tenant_id;
          if (effectiveTenantId && realClinics.length > 0) {
            const defaultClinic = realClinics.find(c => c.id === effectiveTenantId) || realClinics[0];
            setSelectedClinic(defaultClinic);
          } else if (realClinics.length > 0) {
            setSelectedClinic(realClinics[0]);
          }
        } catch (error) {
          console.error('Error fetching clinics:', error);
        }
      }
    };

    fetchClinics();
  }, [searchParams, profile, getEffectiveTenantId]);

  // Fetch users (patients for physicians, physicians for patients)
  useEffect(() => {
    const fetchUsers = async () => {
      if (userType === 'physician' && !selectedClinic) {
        setPeopleList([]);
        return;
      }

      try {
        const roleToFetch = userType === 'physician' ? 'patient' : 'physician';
        // Use getEffectiveTenantId to handle view-as scenarios
        const effectiveTenantId = userType === 'physician'
          ? (selectedClinic?.id || getEffectiveTenantId() || user?.tenant_id)
          : (getEffectiveTenantId() || user?.tenant_id);

        // Don't fetch if we don't have a tenantId
        if (!effectiveTenantId) {
          setPeopleList([]);
          return;
        }

        const { data, error } = await listUsers({
          role: roleToFetch,
          tenantId: effectiveTenantId,
          page: 1,
          pageSize: 100,
        });

        if (error) {
          // Suppress connection errors
          const errorMessage = error?.message || error?.toString() || '';
          const isConnectionError = error?.isConnectionError ||
            errorMessage.includes('Backend server is not available') ||
            errorMessage.includes('Failed to fetch') ||
            errorMessage.includes('ERR_CONNECTION_REFUSED');

          if (!isConnectionError && import.meta.env.DEV) {
            console.error('Error fetching users:', error);
          }
          setPeopleList([]);
          return;
        }

        const users = data?.rows || data?.data || [];
        setPeopleList(users.map(u => ({
          id: u.id,
          name: `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim() || u.email || 'Unknown'
        })));
      } catch (error) {
        // Suppress connection and validation errors
        const errorMessage = error?.message || error?.toString() || '';
        const isSuppressedError = error?.isConnectionError ||
          errorMessage.includes('Backend server is not available') ||
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('ERR_CONNECTION_REFUSED') ||
          errorMessage.includes('tenantId is required');

        if (!isSuppressedError && import.meta.env.DEV) {
          console.error('Error fetching users:', error);
        }
        setPeopleList([]);
      }
    };

    fetchUsers();
  }, [userType, selectedClinic, user, getEffectiveTenantId]);

  // Supplement peopleList with direct lookups for any user IDs in appointments not already resolved
  useEffect(() => {
    if (!appointments || appointments.length === 0) return;

    const knownIds = new Set(peopleList.map(p => p.id));
    const missingIds = new Set();

    appointments.forEach(appt => {
      const providerId = appt.provider_id || appt.physician_id;
      const patientId = appt.patient_id;
      if (providerId && !knownIds.has(providerId)) missingIds.add(providerId);
      if (patientId && !knownIds.has(patientId)) missingIds.add(patientId);
    });

    if (missingIds.size === 0) return;

    const fetchMissingUsers = async () => {
      try {
        const results = await Promise.allSettled(
          [...missingIds].map(id => apiClient.getUserById(id))
        );
        const newPeople = [];
        results.forEach(result => {
          if (result.status === 'fulfilled' && result.value) {
            const u = result.value;
            newPeople.push({
              id: u.id,
              name: `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim() || u.email || 'Unknown'
            });
          }
        });
        if (newPeople.length > 0) {
          setPeopleList(prev => [...prev, ...newPeople]);
        }
      } catch (err) {
        // Silently handle - names just stay as fallback
      }
    };

    fetchMissingUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, peopleList.length]);

  // Fetch available time slots when doctor and date are selected (for patients)
  useEffect(() => {
    const fetchSlots = async () => {
      if (userType !== 'patient' || !selectedPerson || !selectedDate) {
        setAvailableSlots([]);
        setSelectedTime('');
        return;
      }

      setLoadingSlots(true);
      const dateStr = selectedDate.toISOString().split('T')[0];

      try {
        const { data: slots, error } = await getAvailableTimeSlots(
          selectedPerson,
          dateStr,
          dateStr,
          30
        );

        if (!error && slots) {
          const dateSlots = slots.filter(slot => slot.date === dateStr);
          setAvailableSlots(dateSlots);
        } else {
          setAvailableSlots([]);
        }
      } catch (err) {
        // Silently handle errors - slots will just be empty
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [selectedPerson, selectedDate, userType]);

  useEffect(() => {
    if (user) {
      setLoading(true);
      setFetchError('');

      // Fetch initial appointments
      getAppointmentsForUser()
        .then(({ data, error }) => {
          if (error) {
            // Suppress connection errors
            const errorMessage = error?.message || error?.toString() || '';
            const isConnectionError = error?.isConnectionError ||
              errorMessage.includes('Backend server is not available') ||
              errorMessage.includes('Failed to fetch') ||
              errorMessage.includes('ERR_CONNECTION_REFUSED');

            // Only show error for non-connection issues
            if (!isConnectionError) {
              setFetchError('Failed to load appointments. Please try again.');
            }
            setAppointments([]);
            return;
          }

          if (data) {
            // Handle multiple response formats
            let appointmentsArray = [];

            if (Array.isArray(data)) {
              appointmentsArray = data;
            } else if (data.rows && Array.isArray(data.rows)) {
              appointmentsArray = data.rows;
            } else if (data.data && Array.isArray(data.data)) {
              appointmentsArray = data.data;
            }

            setAppointments(appointmentsArray);
            setFetchError('');
          } else {
            setAppointments([]);
          }
        })
        .catch(error => {
          console.error('Error fetching appointments:', error);
          setFetchError('Failed to load appointments. Please try again.');
          setAppointments([]);
        })
        .finally(() => {
          setLoading(false);
        });

      // Connect to socket for real-time updates
      socketClient.connect();

      // Listen for appointment updates
      const handleAppointmentUpdate = (updateData) => {
        setAppointments(prev => {
          // Ensure prev is always an array
          if (!Array.isArray(prev)) {
            return [];
          }
          const index = prev.findIndex(a => a.id === updateData.appointmentId);
          if (index >= 0) {
            // Update existing appointment
            const updated = [...prev];
            updated[index] = { ...updated[index], ...updateData };
            return updated;
          } else {
            // Add new appointment
            return [...prev, updateData];
          }
        });
      };

      socketClient.on('appointment:updated', handleAppointmentUpdate);

      // Cleanup on unmount
      return () => {
        socketClient.off('appointment:updated', handleAppointmentUpdate);
      };
    }
  }, [user]);

  const selectLabel = userType === 'physician' ? 'Select Patient' : 'Select Doctor';
  const pageTitle = userType === 'physician' ? 'Patient Schedule' : 'Book Appointment';
  const buttonText = userType === 'physician' ? 'Schedule Patient' : 'Set Appointment';
  const appointmentListTitle = userType === 'physician' ? 'Today\'s Schedule' : 'Scheduled Appointments';

  // Separate pending requests for physicians
  const pendingRequests = userType === 'physician'
    ? appointments.filter(apt => apt.status === 'pending')
    : [];
  const scheduledAppointments = appointments.filter(apt => apt.status !== 'pending');

  const handleApproveRequest = async (appointmentId) => {
    const { error } = await approveAppointmentRequest(appointmentId);
    if (!error) {
      // Refresh appointments
      if (user) {
        const { data, error: fetchError } = await getAppointmentsForUser();
        if (!fetchError && data) {
          let appointmentsArray = [];
          if (Array.isArray(data)) {
            appointmentsArray = data;
          } else if (data.rows && Array.isArray(data.rows)) {
            appointmentsArray = data.rows;
          } else if (data.data && Array.isArray(data.data)) {
            appointmentsArray = data.data;
          }
          setAppointments(appointmentsArray);
        }
      }
    }
  };

  const handleRejectRequest = async (appointmentId) => {
    const { error } = await rejectAppointmentRequest(appointmentId);
    if (!error) {
      // Refresh appointments
      if (user) {
        const { data, error: fetchError } = await getAppointmentsForUser();
        if (!fetchError && data) {
          let appointmentsArray = [];
          if (Array.isArray(data)) {
            appointmentsArray = data;
          } else if (data.rows && Array.isArray(data.rows)) {
            appointmentsArray = data.rows;
          } else if (data.data && Array.isArray(data.data)) {
            appointmentsArray = data.data;
          }
          setAppointments(appointmentsArray);
        }
      }
    }
  };

  const refreshAppointments = async () => {
    if (user) {
      const { data, error: fetchError } = await getAppointmentsForUser();
      if (!fetchError && data) {
        let appointmentsArray = [];
        if (Array.isArray(data)) {
          appointmentsArray = data;
        } else if (data.rows && Array.isArray(data.rows)) {
          appointmentsArray = data.rows;
        } else if (data.data && Array.isArray(data.data)) {
          appointmentsArray = data.data;
        }
        setAppointments(appointmentsArray);
      }
    }
  };

  const handleCancelClick = (appointment) => {
    setSelectedAppointment(appointment);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedAppointment) return;
    
    const { error } = await cancelAppointment(selectedAppointment.id, cancelReason);
    if (!error) {
      setCancelDialogOpen(false);
      setSelectedAppointment(null);
      setCancelReason('');
      await refreshAppointments();
    }
  };

  const handleRescheduleClick = (appointment) => {
    setSelectedAppointment(appointment);
    if (appointment.scheduled_time) {
      const date = new Date(appointment.scheduled_time);
      setRescheduleDate(date);
      setRescheduleTime(date);
    } else {
      setRescheduleDate(new Date());
      setRescheduleTime(new Date());
    }
    setRescheduleDialogOpen(true);
  };

  const handleRescheduleConfirm = async () => {
    if (!selectedAppointment || !rescheduleDate) return;
    
    // Combine date and time
    let scheduledDateTime = new Date(rescheduleDate);
    if (rescheduleTime) {
      scheduledDateTime.setHours(rescheduleTime.getHours());
      scheduledDateTime.setMinutes(rescheduleTime.getMinutes());
      scheduledDateTime.setSeconds(0);
      scheduledDateTime.setMilliseconds(0);
    } else {
      // Default to 9 AM if no time selected
      scheduledDateTime.setHours(9, 0, 0, 0);
    }

    const { error } = await rescheduleAppointment(selectedAppointment.id, scheduledDateTime.toISOString());
    if (!error) {
      setRescheduleDialogOpen(false);
      setSelectedAppointment(null);
      setRescheduleDate(null);
      setRescheduleTime(null);
      await refreshAppointments();
    }
  };

  const handleDeleteClick = async (appointment) => {
    if (window.confirm('Are you sure you want to remove this appointment from your list?')) {
      const { error } = await deleteAppointment(appointment.id);
      if (!error) {
        // Remove from local state immediately — backend soft-deletes,
        // so refetching would still show it. This removes it from view.
        setAppointments(prev => prev.filter(a => a.id !== appointment.id));
      }
    }
  };

  const handleCancel = () => {
    if (userType === 'physician') {
      navigate('/clinic-dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const handleSetAppointment = async () => {
    setError('');

    // Validation
    if (userType === 'physician' && !selectedClinic) {
      setError('Please select a clinic first.');
      return;
    }

    if (!selectedPerson || !selectedDate) {
      setError(`Please select a ${userType === 'physician' ? 'patient' : 'doctor'} and a date.`);
      return;
    }

    // For patients, require time selection (from slots or fallback picker)
    if (userType === 'patient' && availableSlots.length > 0 && !selectedTime) {
      setError('Please select an available time slot.');
      return;
    }
    if (userType === 'patient' && availableSlots.length === 0 && !selectedTimeDate) {
      setError('Please select a preferred time for your appointment request.');
      return;
    }

    // Map field names to match backend expectations
    const effectiveTenantId = userType === 'physician'
      ? (selectedClinic?.id || getEffectiveTenantId() || user?.tenant_id)
      : user?.tenant_id;

    // Combine date and time
    let scheduledDateTime = selectedDate;
    if (userType === 'patient' && selectedTime) {
      // Patient: use selected time slot
      const [hours, minutes] = selectedTime.split(':');
      scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(parseInt(hours, 10));
      scheduledDateTime.setMinutes(parseInt(minutes, 10));
      scheduledDateTime.setSeconds(0);
      scheduledDateTime.setMilliseconds(0);
    } else if (userType === 'patient' && selectedTimeDate) {
      // Patient fallback: use TimePicker when no slots are available
      scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(selectedTimeDate.getHours());
      scheduledDateTime.setMinutes(selectedTimeDate.getMinutes());
      scheduledDateTime.setSeconds(0);
      scheduledDateTime.setMilliseconds(0);
    } else if (userType === 'physician' && selectedTimeDate) {
      // Physician: use TimePicker value
      scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(selectedTimeDate.getHours());
      scheduledDateTime.setMinutes(selectedTimeDate.getMinutes());
      scheduledDateTime.setSeconds(0);
      scheduledDateTime.setMilliseconds(0);
    } else if (userType === 'physician') {
      // For physicians, set a default time if not specified (e.g., 9 AM)
      scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(9, 0, 0, 0);
    }

    const appointmentData = {
      patient_id: userType === 'physician' ? selectedPerson : user.id,
      provider_id: userType === 'physician' ? user.id : selectedPerson,
      scheduled_time: scheduledDateTime.toISOString(),
      tenant_id: effectiveTenantId,
      // Status is now determined by the backend based on user role
      // status: 'scheduled',
    };

    const { data, error } = await createAppointment(appointmentData);
    if (error) {
      setError(error.message || 'Failed to create appointment.');
      console.error('Appointment creation error:', error);
    } else {
      const newAppt = Array.isArray(data) ? data[0] : data;
      setAppointments(prev => [...prev, newAppt]);
      setSelectedPerson('');
      setSelectedDate(null);
      setSelectedTime('');
      setSelectedTimeDate(null);
      setAvailableSlots([]);

      // Emit via socket for real-time notification
      socketClient.emitAppointmentUpdate({
        appointmentId: newAppt.id,
        patientId: appointmentData.patient_id,
        physicianId: appointmentData.provider_id,
        ...newAppt,
      });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 4,
          maxWidth: 800,
          width: '100%',
          mx: 'auto',
          position: 'relative',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          animation: `${fadeIn} 0.5s ease-out`,
          overflow: 'hidden',
        }}
      >
        <IconButton
          aria-label="Cancel appointment scheduling"
          onClick={handleCancel}
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          flexDirection: 'column'
        }}>
          <Avatar
            sx={{
              bgcolor: 'primary.main',
              width: 56,
              height: 56,
              mb: 2,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <CalendarMonthIcon fontSize="large" />
          </Avatar>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              textAlign: 'center'
            }}
          >
            {pageTitle}
          </Typography>
        </Box>
        {/* Clinic Selector for Physicians */}
        {userType === 'physician' && clinics.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="clinic-select-label">Select Clinic</InputLabel>
              <Select
                labelId="clinic-select-label"
                value={selectedClinic?.id || ''}
                label="Select Clinic"
                onChange={e => {
                  const clinic = clinics.find(c => c.id === e.target.value);
                  setSelectedClinic(clinic);
                  setSelectedPerson(''); // Reset patient selection when clinic changes
                }}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                {clinics.map(clinic => (
                  <MenuItem key={clinic.id} value={clinic.id}>
                    {clinic.name || clinic.clinic_name || `Clinic ${clinic.id.slice(0, 8)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ mb: 2 }} disabled={userType === 'physician' && !selectedClinic}>
            <InputLabel id="person-select-label">{selectLabel}</InputLabel>
            <Select
              labelId="person-select-label"
              value={selectedPerson}
              label={selectLabel}
              onChange={e => setSelectedPerson(e.target.value)}
              disabled={userType === 'physician' && !selectedClinic}
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }}
            >
              {peopleList.length === 0 ? (
                <MenuItem disabled>
                  {userType === 'physician' && !selectedClinic
                    ? 'Please select a clinic first'
                    : 'No options available'}
                </MenuItem>
              ) : (
                peopleList.map(person => (
                  <MenuItem key={person.id} value={person.id}>{person.name}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ mb: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={(newDate) => {
                setSelectedDate(newDate);
                setSelectedTime(''); // Reset time when date changes
                setSelectedTimeDate(null); // Reset time picker
              }}
              minDate={new Date()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: {
                    mb: 2,
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* Time slot selector for patients when doctor is selected */}
        {userType === 'patient' && selectedPerson && selectedDate && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
              Select Available Time Slot
            </Typography>
            {loadingSlots ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Loading available times...
                </Typography>
              </Box>
            ) : availableSlots.length === 0 ? (
              <>
                <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
                  {selectedDate
                    ? 'No pre-set time slots for this date. The doctor may not have set their availability yet, or all slots are booked. You can still request an appointment by selecting a preferred time below.'
                    : 'Please select a date first'}
                </Alert>
                {selectedDate && (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                      label="Select Preferred Time"
                      value={selectedTimeDate}
                      onChange={(newTime) => setSelectedTimeDate(newTime)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { borderRadius: 2 }
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Your appointment request will be sent to the doctor for approval.
                    </Typography>
                  </LocalizationProvider>
                )}
              </>
            ) : (
              <>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 1.5,
                  mb: 1.5
                }}>
                  {availableSlots.map((slot) => {
                    const timeValue = slot.start_time;
                    const isSelected = selectedTime === timeValue;

                    // Format time for display (12-hour format)
                    const formatTime = (timeStr) => {
                      const [hours, minutes] = timeStr.split(':');
                      const hour = parseInt(hours, 10);
                      const ampm = hour >= 12 ? 'PM' : 'AM';
                      const displayHour = hour % 12 || 12;
                      return `${displayHour}:${minutes} ${ampm}`;
                    };

                    const startTimeFormatted = formatTime(slot.start_time);
                    const endTimeFormatted = formatTime(slot.end_time);

                    return (
                      <Button
                        key={timeValue}
                        variant={isSelected ? 'contained' : 'outlined'}
                        onClick={() => setSelectedTime(timeValue)}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 1, sm: 2 },
                          borderRadius: 2,
                          borderColor: isSelected ? 'primary.main' : 'rgba(0, 0, 0, 0.1)',
                          bgcolor: isSelected ? 'primary.main' : 'transparent',
                          color: isSelected ? 'white' : 'text.primary',
                          fontWeight: isSelected ? 600 : 400,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: isSelected ? 'primary.dark' : 'primary.light',
                            color: isSelected ? 'white' : 'primary.main',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {isMobile ? startTimeFormatted : `${startTimeFormatted} - ${endTimeFormatted}`}
                      </Button>
                    );
                  })}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  {availableSlots.length} available slot{availableSlots.length !== 1 ? 's' : ''} on this date
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Time picker for physicians */}
        {userType === 'physician' && selectedPerson && selectedDate && (
          <Box sx={{ mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                label="Select Time"
                value={selectedTimeDate}
                onChange={(newTime) => {
                  setSelectedTimeDate(newTime);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: {
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.1)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                    }
                  }
                }}
              />
            </LocalizationProvider>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Select the appointment time for this patient
            </Typography>
          </Box>
        )}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{
            mb: 3,
            py: 1.5,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
            }
          }}
          onClick={handleSetAppointment}
        >
          {buttonText}
        </Button>
        {error && (
          <Typography color="error" sx={{ mb: 3 }}>{error}</Typography>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Pending Requests Section (for physicians) */}
        {userType === 'physician' && pendingRequests.length > 0 && (
          <>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              Pending Appointment Requests
            </Typography>
            <Box sx={{ mb: 4 }}>
              {pendingRequests.map(appt => {
                const patient = peopleList.find(p => p.id === appt.patient_id);
                return (
                  <Box
                    key={appt.id}
                    sx={{
                      display: 'flex',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      p: { xs: 1.5, sm: 2 },
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: 'warning.light',
                      border: '1px solid',
                      borderColor: 'warning.main',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      gap: { xs: 1.5, sm: 0 }
                    }}
                  >
                    <Avatar sx={{ bgcolor: 'warning.main', mr: { xs: 0, sm: 2 }, width: { xs: 40, sm: 40 }, height: { xs: 40, sm: 40 } }}>
                      <PersonIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </Avatar>
                    <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {patient?.name || 'Unknown Patient'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {new Date(appt.scheduled_time || appt.appointment_time).toLocaleString()}
                      </Typography>
                      {appt.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          {appt.notes}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 0.5, sm: 1 },
                      width: { xs: '100%', sm: 'auto' },
                      justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                      mt: { xs: 1, sm: 0 }
                    }}>
                      <Chip label="Pending" color="warning" size="small" sx={{ mr: { xs: 0, sm: 2 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
                      <Button
                        variant="contained"
                        color="success"
                        size={isMobile ? 'small' : 'small'}
                        startIcon={!isMobile ? <CheckCircleIcon /> : null}
                        onClick={() => handleApproveRequest(appt.id)}
                        sx={{ 
                          mr: { xs: 0.5, sm: 1 },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        {isMobile ? 'Approve' : 'Approve'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size={isMobile ? 'small' : 'small'}
                        startIcon={!isMobile ? <CancelIcon /> : null}
                        onClick={() => handleRejectRequest(appt.id)}
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        {isMobile ? 'Reject' : 'Reject'}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Divider sx={{ mb: 3 }} />
          </>
        )}

        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 600,
            textAlign: 'center',
            color: 'text.primary'
          }}
        >
          {appointmentListTitle}
        </Typography>

        <Box sx={{ mb: 2 }}>
          {loading ? (
            <SkeletonLoader variant="list" count={3} />
          ) : fetchError ? (
            <ErrorMessage
              title="Unable to load appointments"
              message={fetchError}
              actionLabel="Retry"
              onAction={() => navigate(0)}
              severity="error"
            />
          ) : !Array.isArray(appointments) ? (
            <ErrorMessage
              title="Data format error"
              message="The appointments data format is incorrect. Please refresh the page."
              actionLabel="Refresh"
              onAction={() => navigate(0)}
              severity="warning"
            />
          ) : scheduledAppointments.length === 0 ? (
            <EmptyState
              title={userType === 'physician' ? 'No patients scheduled today' : 'No appointments scheduled yet'}
              description={userType === 'physician'
                ? "You don't have any patients scheduled for today. Check back later or schedule new appointments."
                : "You don't have any appointments scheduled yet. Book your first appointment to get started."}
              variant="illustrated"
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {scheduledAppointments.map(appt => {
                if (!appt) return null;
                const scheduledTime = appt.scheduled_time || appt.appointment_time;
                let formattedDate = 'Time TBD';

                if (scheduledTime) {
                  try {
                    const date = new Date(scheduledTime);
                    if (!isNaN(date.getTime())) {
                      formattedDate = date.toLocaleString();
                    }
                  } catch (e) {
                    console.error('Invalid date:', scheduledTime);
                  }
                }

                const patientName = peopleList.find(p => p.id === appt.patient_id)?.name || 'Unknown Patient';
                const rawProviderName = peopleList.find(p => p.id === appt.provider_id || p.id === appt.physician_id)?.name;
                const providerName = rawProviderName ? (rawProviderName.startsWith('Dr.') ? rawProviderName : `Dr. ${rawProviderName}`) : 'Unknown Physician';

                return (
                  <Box
                    key={appt.id || Math.random()}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      }
                    }}
                  >
                    <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {userType === 'physician' ? patientName : providerName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formattedDate}
                      </Typography>
                      {appt.status && (
                        <Chip
                          label={appt.status}
                          size="small"
                          color={
                            appt.status === 'confirmed' || appt.status === 'scheduled' ? 'success'
                            : appt.status === 'pending' ? 'warning'
                            : appt.status === 'cancelled' || appt.status === 'rejected' ? 'error'
                            : appt.status === 'in_progress' ? 'info'
                            : 'default'
                          }
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {appt.status === 'scheduled' || appt.status === 'confirmed' ? (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => navigate(`/video-call?roomId=${appt.video_call_room_id || appt.id}`)}
                        >
                          Join Call
                        </Button>
                      ) : null}
                      {(appt.status === 'scheduled' || appt.status === 'confirmed' || appt.status === 'pending') && (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleRescheduleClick(appt)}
                            title="Reschedule"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelClick(appt)}
                            title="Cancel"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {(appt.status === 'cancelled' || appt.status === 'completed') && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(appt)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Cancel Appointment Dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Cancel Appointment</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to cancel this appointment? This action can be undone by rescheduling.
            </DialogContentText>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for cancellation (optional)"
              placeholder="e.g., Patient requested, scheduling conflict, etc."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>Keep Appointment</Button>
            <Button onClick={handleCancelConfirm} color="error" variant="contained">
              Cancel Appointment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reschedule Appointment Dialog */}
        <Dialog open={rescheduleDialogOpen} onClose={() => setRescheduleDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reschedule Appointment</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Select a new date and time for this appointment.
            </DialogContentText>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="New Date"
                value={rescheduleDate}
                onChange={setRescheduleDate}
                minDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { mb: 2 }
                  }
                }}
              />
              <TimePicker
                label="New Time"
                value={rescheduleTime}
                onChange={setRescheduleTime}
                slotProps={{
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRescheduleDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleRescheduleConfirm} 
              variant="contained"
              disabled={!rescheduleDate}
            >
              Reschedule
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default Appointments;
